import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { CalendarDays, DollarSign, Users, FileText, ArrowUpRight, ArrowDownRight, TrendingUp, Plus, Bug, Calculator } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { computeKPIs, fmt } from '../../lib/accountingStore';
import { apiInvoices, apiCustomers, apiExpenses, apiWorkorders, apiPayments, team } from '../../lib/api';
import { daysBetween, isToday, parseHour, shiftDays, todayISO, weekOf, ymd } from '../../lib/dates';
import { statusBadge, useJobStatuses } from '../../lib/jobStatus';

const iconMap = { CalendarDays, DollarSign, Users, FileText };
const colorMap = {
  blue: 'bg-blue-50 text-blue-600',
  teal: 'bg-teal-50 text-teal-600',
  green: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  purple: 'bg-purple-50 text-purple-600',
  orange: 'bg-orange-50 text-orange-600',
};

// Badge styling is derived from the status group in lib/jobStatus.

// Money as written. An imported figure can arrive as "$1,200.50"; Number() of
// that is NaN, which silently becomes zero on a revenue figure.
const money = (v) => {
  if (typeof v === 'number') return isFinite(v) ? v : 0;
  const n = parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return isNaN(n) ? 0 : n;
};

/**
 * Payments received per week, for the 12 weeks ending this one.
 *
 * This used to be a hardcoded array of twelve numbers drawn under the heading
 * "Revenue Trend - Last 12 weeks". It was a picture of nothing.
 */
const weeklyRevenue = (paymentRows) => {
  const thisWeek = weekOf(todayISO())[0];
  const firstWeek = shiftDays(thisWeek, -7 * 11);
  const buckets = Array(12).fill(0);
  for (const p of paymentRows || []) {
    const day = ymd(p.date);
    if (!day) continue;
    const i = Math.floor(daysBetween(firstWeek, day) / 7);
    if (i >= 0 && i < 12) buckets[i] += money(p.amount);
  }
  return { buckets, firstWeek, total: buckets.reduce((a, b) => a + b, 0) };
};

const MiniChart = ({ points }) => {
  if (!points || points.length < 2) return <div className="h-24"/>;
  const max = Math.max(...points);
  if (max <= 0) {
    return <div className="h-24 flex items-center justify-center text-sm text-slate-400">
      No payments recorded in this period.
    </div>;
  }
  const w = 100 / (points.length - 1);
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${i * w} ${100 - (p / max) * 80}`).join(' ');
  const area = `${path} L 100 100 L 0 100 Z`;
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-24">
      <defs>
        <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8BC53F" stopOpacity="0.35"/>
          <stop offset="100%" stopColor="#8BC53F" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={area} fill="url(#g1)"/>
      <path d={path} fill="none" stroke="#8BC53F" strokeWidth="1.5" vectorEffect="non-scaling-stroke"/>
    </svg>
  );
};

export default function DashboardHome({ user }) {
  useJobStatuses();   // populates the cache statusBadge reads
  const [acct, setAcct] = useState({ invoices: [], customers: [], expenses: [], bills: [] });
  const [jobs, setJobs] = useState([]);
  const [custCount, setCustCount] = useState(0);
  const [techs, setTechs] = useState([]);
  const [pays, setPays] = useState([]);
  useEffect(() => {
    Promise.all([apiInvoices.list(), apiCustomers.list(), apiExpenses.list(), apiWorkorders.list(), apiPayments.list()]).then(([inv, cust, exp, wo, pay]) => {
      setAcct({ invoices: inv || [], customers: cust || [], expenses: exp || [], bills: [] });
      setCustCount(cust ? cust.length : 0);
      setJobs(wo || []);
      setPays(pay || []);
    }).catch(()=>{});
    team.technicians().then(setTechs).catch(()=>{});
  }, []);
  const k = computeKPIs(acct);
  // The tile said "Today's Jobs" and printed the length of every work order ever
  // recorded - 1,152 of them on a day with four real visits, and 877 the evening
  // a year of route work was imported from a calendar. Sorted by time because
  // the technician strip and the schedule list below both read it in order.
  const todaysJobs = jobs
    .filter(j => isToday(j.date))
    .sort((a, b) => (parseHour(a.time) ?? 99) - (parseHour(b.time) ?? 99));
  const revenue = weeklyRevenue(pays);
  const palette = ['bg-[#8BC53F]', 'bg-blue-500', 'bg-amber-500', 'bg-purple-500', 'bg-teal-500', 'bg-orange-500'];
  const techStats = techs.map((t, i) => {
    const mine = todaysJobs.filter(j => (t.techId && j.techId === t.techId) || (j.tech && String(j.tech).toLowerCase() === String(t.name).toLowerCase()));
    const done = mine.filter(j => j.status === 'Completed').length;
    // Only real words: "Ish (Field Tech)" was coming out as "I(".
    const initials = (String(t.name || '?').match(/[A-Za-z]+/g) || ['?'])
      .slice(0, 2).map(w => w[0]).join('').toUpperCase();
    return { name: t.name, initials, jobs: mine.length, completed: done, color: palette[i % palette.length] };
  });
  // `to` on each tile: all four printed a number and did nothing when tapped,
  // which on a phone is the only thing there is to tap.
  const liveStats = [
    { label: "Today\u2019s Jobs", value: String(todaysJobs.length), icon: 'CalendarDays', color: 'green', to: '/app/schedule' },
    { label: 'Revenue Collected', value: fmt(k.income), icon: 'DollarSign', color: 'amber', to: '/app/payments' },
    { label: 'Active Customers', value: custCount ? custCount.toLocaleString() : acct.customers.length.toString(), icon: 'Users', color: 'purple', to: '/app/customers' },
    { label: 'Open A/R', value: fmt(k.ar), icon: 'FileText', color: 'orange', to: '/app/collections' },
  ];

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-[1600px]">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 capitalize">Welcome back, {user?.name}</h1>
          <p className="text-slate-500 mt-1">Here’s what’s happening with your business today.</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" className="gap-1.5"><Link to="/app/schedule"><Icons.Calendar className="w-4 h-4"/>Today</Link></Button>
          <Button asChild className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5"><Link to="/app/schedule"><Plus className="w-4 h-4"/>New Work Order</Link></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {liveStats.map(s => {
          const Ico = iconMap[s.icon];
          const c = colorMap[s.color];
          // These were plain cards: the office tapped them and nothing
          // happened. min-w-0 + truncate so a long figure cannot push the
          // tile wider than the phone.
          return (
            <Link key={s.label} to={s.to} className="block min-w-0 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8BC53F]">
              <Card className="pp-lift p-4 sm:p-5 border-slate-200 h-full hover:border-[#8BC53F] transition-colors">
                {/* Two of these sit side by side on a 390px phone. The label
                    wraps rather than truncating, and the figure is never cut:
                    "$4,…" is not a number anyone can act on. */}
                <div className="flex items-start justify-between gap-2">
                  <div className="text-xs sm:text-sm text-slate-500 leading-tight">{s.label}</div>
                  <div className={`w-8 h-8 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${c || 'bg-slate-50 text-slate-600'}`}>
                    <Ico className="w-4 h-4 sm:w-5 sm:h-5"/>
                  </div>
                </div>
                <div className="text-xl sm:text-3xl font-extrabold text-slate-900 mt-2 tabular-nums break-words">{s.value}</div>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* min-w-0 on the children: a grid item defaults to min-width:auto, so it
          refuses to shrink below its own content. One long job row was enough to
          make this grid 529px wide, which widened the page to 553 and left the
          phone showing a zoomed-out dashboard with half the screen unused.
          Measured at 390x844: 553 before, 390 after. */}
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 min-w-0 p-6 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Today’s Schedule</h2>
              <p className="text-sm text-slate-500">{todaysJobs.length === 0 ? 'Nothing booked for today' : `${todaysJobs.length} job${todaysJobs.length === 1 ? '' : 's'} today`}</p>
            </div>
            <Link to="/app/schedule" className="text-sm font-semibold text-[#8BC53F] hover:underline">View All →</Link>
          </div>
          <div className="space-y-2">
            {todaysJobs.length === 0 && (
              <p className="text-sm text-slate-400 py-6 text-center">
                No work orders scheduled for today. <Link to="/app/schedule" className="text-[#8BC53F] font-semibold hover:underline">Open the schedule</Link>
              </p>
            )}
            {todaysJobs.slice(0, 5).map(j => (
              <Link key={j.id} to="/app/schedule" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition border border-transparent hover:border-slate-200">
                {/* w-16 was not wide enough for the reference underneath, so it
                    ran under the customer name. It truncates now. */}
                <div className="text-center w-20 flex-shrink-0 min-w-0">
                  <div className="text-sm font-bold text-slate-900">{j.time || '—'}</div>
                  <div className="text-[10px] text-slate-500 font-mono truncate">{j.workOrderNumber || j.id}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{j.customer}</div>
                  <div className="text-xs text-slate-500 truncate">{[j.service || j.name, j.address].filter(Boolean).join(' · ')}</div>
                </div>
                <div className="text-xs text-slate-600 hidden md:block flex-shrink-0">{j.tech||'—'}</div>
                <Badge className={`${statusBadge(j.status)} font-medium border-0 flex-shrink-0`}>{j.status}</Badge>
              </Link>
            ))}
          </div>
        </Card>

        <Card className="p-6 border-slate-200 min-w-0">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Technicians</h2>
              <p className="text-xs text-slate-500">Today</p>
            </div>
            <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{techStats.length} Active</span>
          </div>
          <div className="space-y-4">
            {techStats.length === 0 && <p className="text-sm text-slate-400">No technicians yet. Add them in Settings → Team.</p>}
            {techStats.map(t => {
              const pct = t.jobs ? (t.completed / t.jobs) * 100 : 0;
              return (
                <div key={t.name}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 flex-shrink-0 rounded-full ${t.color} text-white font-bold flex items-center justify-center text-sm`}>{t.initials}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-slate-900 truncate">{t.name}</div>
                      <div className="text-xs text-slate-500">{t.completed}/{t.jobs} jobs done</div>
                    </div>
                  </div>
                  <Progress value={pct} className="h-1.5 mt-2"/>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* min-w-0 on the children: a grid item defaults to min-width:auto, so it
          refuses to shrink below its own content. One long job row was enough to
          make this grid 529px wide, which widened the page to 553 and left the
          phone showing a zoomed-out dashboard with half the screen unused.
          Measured at 390x844: 553 before, 390 after. */}
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 min-w-0 p-6 border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Revenue Trend</h2>
              <p className="text-sm text-slate-500">Payments received, last 12 weeks</p>
            </div>
            <div className="text-right">
              {/* The figure beside the chart is the one the chart draws. This
                  said "Collected to date" over a 12-week heading. */}
              <div className="text-2xl font-extrabold text-slate-900">{fmt(revenue.total)}</div>
              <div className="text-xs text-emerald-600 font-semibold flex items-center gap-1 justify-end"><TrendingUp className="w-3.5 h-3.5"/>Since {new Date(revenue.firstWeek + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
            </div>
          </div>
          <MiniChart points={revenue.buckets}/>
        </Card>

        <Card className="p-6 border-slate-200 bg-gradient-to-br from-[#8BC53F] to-[#6FA82C] text-white relative overflow-hidden min-w-0">
          <div className="absolute inset-0 pp-dot-grid opacity-30"/>
          <div className="relative">
            <div className="w-11 h-11 rounded-lg bg-white/15 flex items-center justify-center mb-3">
              <Calculator className="w-5 h-5"/>
            </div>
            <h3 className="text-xl font-bold">Accounting Snapshot</h3>
            <p className="text-sm text-white/80 mt-1">Full accounting built in</p>
            <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
              <div className="bg-white/10 rounded-lg p-3">
                <div className="text-white/70 text-xs">Income</div>
                <div className="font-bold text-lg">{fmt(k.income)}</div>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <div className="text-white/70 text-xs">Expenses</div>
                <div className="font-bold text-lg">{fmt(k.expenses)}</div>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <div className="text-white/70 text-xs">A/R Open</div>
                <div className="font-bold text-lg">{fmt(k.ar)}</div>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <div className="text-white/70 text-xs">Net Profit</div>
                <div className="font-bold text-lg">{fmt(k.profit)}</div>
              </div>
            </div>
            <Button asChild className="mt-5 w-full bg-white text-[#8BC53F] hover:bg-slate-100 font-bold">
              <Link to="/app/accounting">Open Accounting →</Link>
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
